import { http, HTTPError } from './common';
import { setFileContent } from './api';
import { Schema as AnySchema, Validator } from 'jsonschema';
import { minimatch } from 'minimatch';
import { toastSubject } from '~shared/utils/subjects';

export const SHARED_API_PREFIX = '/$shared';

const sharedResources: {
  [key: string]:
    | {
        parser: 'json';
        content: unknown;
        schema?: AnySchema;
      }
    | undefined;
} = {
  '**/saved_conditions.json': {
    parser: 'json',
    content: {
      conditions: [],
    },
    schema: {
      type: 'object',
      properties: {
        conditions: {
          type: 'array',
          items: {
            type: 'object',
          },
        },
      },
      required: ['conditions'],
    },
  },
  '**/shared_datasources.json': {
    parser: 'json',
    content: {},
    schema: {
      type: 'object',
      additionalProperties: true,
      patternProperties: {
        '^.*$': {
          type: 'array',
        },
      },
    },
  },
  '**/shared_css.json': {
    parser: 'json',
    content: {
      classes: [],
    },
    schema: {
      type: 'object',
      properties: {
        classes: {
          type: 'array',
          items: {
            type: 'object',
          },
        },
      },
      required: ['classes'],
    },
  },
  '**/CustomComponents/manifest.json': {
    parser: 'json',
    content: [],
    schema: {
      type: 'array',
      items: {
        type: 'object',
      },
    },
  },
  '**/crafted_components.json': {
    parser: 'json',
    content: [],
    schema: { type: 'array' },
  },
  '**/HTTPHandlers.json': {
    parser: 'json',
    content: [],
    schema: { type: 'array' },
  },
  '**/*.json': {
    parser: 'json',
    content: {},
  },
};

function getResource(path: string) {
  const found = Object.entries(sharedResources).find(([key]) =>
    minimatch(path, key),
  );
  return found?.[1];
}

export function tryCreateSharedResource(path: string) {
  const content = getResource(path)?.content;
  if (!content) return;
  const name = path.replace(`${SHARED_API_PREFIX}/`, 'Shared/');
  setFileContent({
    name,
    content: content as string,
    asText: typeof content === 'string',
  });
}

export interface IGetSharedResourceRequest {
  path: string; // relative path
  createIfNotExists?: boolean;
}

export function getSharedResource<T>({
  path,
  createIfNotExists = false,
}: IGetSharedResourceRequest) {
  const filePath = `${SHARED_API_PREFIX}/${path}`;
  return http<T>(`${filePath}?t=${Date.now()}`, {
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
  })
    .then((response) => {
      const resource = getResource(path);
      let res = response;
      if (!resource) return res;
      if (typeof res === 'string') {
        try {
          switch (resource.parser) {
            case 'json':
              res = JSON.parse(res) as T;
              break;

            default:
              break;
          }
        } catch (e) {
          throw new Error('Cannot parse');
        }
      }
      if (resource.schema) {
        const v = new Validator();
        if (!v.validate(res, resource.schema).valid) {
          throw new Error('Invalid Schema');
        }
      }
      return res;
    })
    .catch((e) => {
      if (isFailureError(e) && createIfNotExists) {
        const message = parseErrorMessage(e, path);
        toastSubject.next({
          message,
          kind: 'warning',
        });
        tryCreateSharedResource(filePath);
      }
      throw e;
    });
}

function isFailureError(e: HTTPError) {
  return (
    e?.response?.status === 404 ||
    e?.message === 'Invalid Schema' ||
    e?.message === 'Cannot parse' ||
    e?.name === 'SyntaxError'
  );
}

function parseErrorMessage(e: HTTPError, path: string) {
  switch (true) {
    case e?.message === 'Cannot parse ' || e?.name === 'SyntaxError':
      return `Cannot parse file '${path}', resetting...`;
    case e?.message === 'Invalid Schema':
      return `File '${path}' is corrupt, resetting...`;
    case e?.response?.status === 404:
      return `File '${path}' not found, creating...`;
    default:
      return e?.message;
  }
}
